import { useState } from 'react';
import { toast } from 'sonner';
import { useActionsTresorerie, useSituationTresorerie, useSituationsTransmises } from '@/api/hooks';
import type { ContenuSituation } from '@/api/types';
import { useSession } from '@/auth/session';
import { BoutonExcel, BoutonPdf, Chargement, ErreurBloc, Kpi, PageHeader, Vide } from '@/components/common';
import { OngletsTresorerie, aujourdhui, estResponsableTresorerie, libelleSemaine } from '@/components/tresorerie';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn, dateFr, dateHeureFr, montant } from '@/lib/utils';

function TableSituation({ s }: { s: ContenuSituation }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Compte</TableHead>
          <TableHead className="text-right">Relevé</TableHead>
          <TableHead className="hidden text-right md:table-cell">Payés non au relevé</TableHead>
          <TableHead className="text-right">Disponible</TableHead>
          <TableHead className="hidden text-right sm:table-cell">Postés</TableHead>
          <TableHead className="hidden text-right sm:table-cell">Programmés</TableHead>
          <TableHead className="text-right">Prévisionnel</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {s.lignes.map((l) => (
          <TableRow key={l.compteId} className={cn(l.sousMinimum && 'bg-red-50')}>
            <TableCell>
              <div className="font-medium">
                {l.banque} · {l.code}
              </div>
              <div className="text-xs text-muted-foreground">
                {l.type === 'CAISSE' ? 'Caisse' : l.intitule} · relevé {l.dateReleve ? `du ${dateFr(l.dateReleve)}` : 'non saisi'} · minimum {montant(l.soldeMinimum)}
              </div>
            </TableCell>
            <TableCell className="num text-right">{montant(l.soldeReleve, true)}</TableCell>
            <TableCell className="num hidden text-right md:table-cell">{montant(l.payesNonAuReleve, true)}</TableCell>
            <TableCell className={cn('num text-right font-semibold', l.soldeDisponible < 0 && 'text-red-700')}>{montant(l.soldeDisponible, true)}</TableCell>
            <TableCell className="num hidden text-right sm:table-cell">{montant(l.postesEnAttente, true)}</TableCell>
            <TableCell className="num hidden text-right sm:table-cell">{montant(l.programmesSemaine, true)}</TableCell>
            <TableCell className={cn('num text-right font-semibold', l.sousMinimum && 'text-red-700')}>
              {montant(l.soldePrevisionnel, true)}
              {l.sousMinimum && <div className="text-xs font-normal">sous le minimum</div>}
            </TableCell>
          </TableRow>
        ))}
        <TableRow className="font-semibold">
          <TableCell>Total</TableCell>
          <TableCell />
          <TableCell className="hidden md:table-cell" />
          <TableCell className="num text-right">{montant(s.totalDisponible, true)}</TableCell>
          <TableCell className="hidden sm:table-cell" />
          <TableCell className="hidden sm:table-cell" />
          <TableCell className="num text-right">{montant(s.totalPrevisionnel, true)}</TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
}

/** Situation quotidienne de la trésorerie (RT-6, § 11) : calculée en direct, transmise et figée par le responsable, historique. */
export function SituationTresorerie() {
  const session = useSession();
  const profils = session?.utilisateur.profils ?? [];
  const [date, setDate] = useState(aujourdhui());
  const { data, isLoading, error } = useSituationTresorerie(date === aujourdhui() ? undefined : date);
  const historique = useSituationsTransmises();
  const actions = useActionsTresorerie();
  const [archive, setArchive] = useState<string | null>(null);
  const transmettre = async () => {
    try {
      await actions.transmettreSituation.mutateAsync();
      toast.success('Situation transmise au DG, aux DGA et au DF.');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Transmission impossible.');
    }
  };
  const archivee = historique.data?.find((s) => s.id === archive) ?? null;

  return (
    <div>
      <PageHeader
        titre="Situation de la trésorerie"
        sousTitre={data ? `${dateFr(data.date)} · ${libelleSemaine(data.semaine)}` : undefined}
        actions={
          <>
            <Input type="date" value={date} max={aujourdhui()} onChange={(e) => e.target.value && setDate(e.target.value)} className="w-auto" aria-label="Date" />
            <BoutonPdf chemin={`/pdf/tresorerie/situation?date=${date}`} nom={`situation-tresorerie-${date}.pdf`} />
            <BoutonExcel chemin={`/tresorerie/situation.xlsx?date=${date}`} nom={`tresorerie-situation-${date}.xlsx`} taille="default" />
            {estResponsableTresorerie(profils) && date === aujourdhui() && data && !data.transmise && (
              <Button onClick={transmettre} disabled={actions.transmettreSituation.isPending}>
                Transmettre la situation du jour
              </Button>
            )}
          </>
        }
      />
      <OngletsTresorerie />
      {isLoading ? (
        <Chargement />
      ) : error || !data ? (
        <ErreurBloc erreur={error} />
      ) : (
        <div className="space-y-6">
          {data.transmise ? (
            <Alert>
              <AlertDescription>
                Situation transmise le {dateHeureFr(data.transmise.createdAt)} par {data.transmise.transmiseParNom} ; la version ci-dessous est recalculée en direct, la version figée figure dans l'historique.
              </AlertDescription>
            </Alert>
          ) : (
            date === aujourdhui() && <div className="text-sm text-muted-foreground">Situation du jour non encore transmise. Elle est calculée en direct à partir des relevés et des paiements.</div>
          )}
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            <Kpi label="Disponible" valeur={montant(data.totalDisponible)} />
            <Kpi label="Prévisionnel fin de semaine" valeur={montant(data.totalPrevisionnel)} accent={data.totalPrevisionnel < 0 ? 'critique' : undefined} />
            <Kpi label="Décaissements du jour" valeur={montant(data.decaissementsDuJour)} />
            <Kpi label="Programmés cette semaine" valeur={montant(data.decaissementsSemaine)} />
            <Kpi label="Postés en attente de validation" valeur={montant(data.enAttenteValidation)} />
          </div>
          {data.alertes.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {data.alertes.map((a, i) => (
                <Badge key={i} variant="destructive" className="whitespace-normal">
                  {a}
                </Badge>
              ))}
            </div>
          )}
          <Card>
            <CardHeader>
              <CardTitle>Soldes par compte</CardTitle>
              <CardDescription>Disponible = relevé − paiements confirmés après le relevé ; prévisionnel = disponible − postés en attente − programmés de la semaine.</CardDescription>
            </CardHeader>
            <CardContent>
              <TableSituation s={data} />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Situations transmises</CardTitle>
              <CardDescription>Versions figées à la transmission, archivées.</CardDescription>
            </CardHeader>
            <CardContent>
              {!historique.data?.length ? (
                <Vide message="Aucune situation transmise." />
              ) : (
                <ul className="divide-y text-sm">
                  {historique.data.map((s) => (
                    <li key={s.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                      <span>
                        <button type="button" className="font-medium text-primary hover:underline" onClick={() => setArchive(archive === s.id ? null : s.id)}>
                          {dateFr(s.date)}
                        </button>
                        <span className="ml-2 text-xs text-muted-foreground">
                          {s.transmiseParNom} · {dateHeureFr(s.createdAt)}
                        </span>
                      </span>
                      <span className="num">
                        disponible {montant(s.contenu.totalDisponible)} · prévisionnel {montant(s.contenu.totalPrevisionnel)} · {s.contenu.alertes.length} alerte(s)
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              {archivee && (
                <div className="mt-4 rounded-md border p-3">
                  <div className="mb-2 text-sm font-medium">Situation figée du {dateFr(archivee.date)}</div>
                  <TableSituation s={archivee.contenu} />
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
